import { ICellRendererParams } from "ag-grid-enterprise";
import { shallowEqual } from "react-redux";

import { IgCheckbox, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { GenericFlexStartContainer } from "@src/components/MainApp/SharedProductPicker/list/containers";
import { useDispatch, useSelector } from "@src/store";
import {
  CHECKOUT_BLOCK_UPSELL_MAX_PRODUCTS,
  getProductPickerData,
  getProductPickerSelectedCount,
  toggleProduct,
  toggleProductVariant,
} from "@src/store/slices/productPicker";
import { getProductPickerProductCheckboxState } from "@src/store/slices/productPicker/selectors/getProductPickerProductCheckboxState";
import { isProductPickerVariantSelected } from "@src/store/slices/productPicker/selectors/isProductPickerVariantSelected";
import {
  ProductPickerProduct,
  ProductPickerProductVariant,
  ProductPickerSelectedProduct,
} from "@src/store/slices/productPicker/types";
import { bundlePriceTesting } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface Context {
  parentProduct?: ProductPickerProduct;
}

export const ProductListCheckBox = ({
  data,
  context,
}: ICellRendererParams<
  ProductPickerSelectedProduct | ProductPickerProductVariant,
  Context
>) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const productPickerData = useSelector(getProductPickerData);

  const isVariantRow = Boolean(context?.parentProduct);
  const product = (
    isVariantRow ? context!.parentProduct : data
  ) as ProductPickerProduct;
  const variant = isVariantRow ? (data as ProductPickerProductVariant) : null;
  const allVariantIds = product.variants?.map((v) => v.id) ?? [];

  const isVariantSelected = useSelector((state) =>
    isProductPickerVariantSelected(product.id, variant?.id)(state)
  );
  // getProductPickerProductCheckboxState returns a fresh { checked,
  // indeterminate } object each call, so compare shallowly to avoid the
  // react-redux "selector returned a different result" warning and needless
  // re-renders (one per product row).
  const checkboxState = useSelector(
    (state) =>
      getProductPickerProductCheckboxState(product.id, allVariantIds)(state),
    shallowEqual
  );

  const checked = isVariantRow ? isVariantSelected : checkboxState.checked;
  const indeterminate = isVariantRow ? false : checkboxState.indeterminate;

  const bundleProductsConfig =
    useConfiguration<boolean>("enable_bundle_products")?.value ?? false;
  const isPriceTest = productPickerData?.type === "agProducts";
  const isCartTargeting = productPickerData?.type === "cartTargeting";
  const isCheckoutBlock =
    productPickerData?.type === "checkoutUpsellProductAction";
  const isBundleAlwaysBlocked = isCheckoutBlock || isCartTargeting;
  const isBundleDisabled =
    Boolean(product?.isBundle) &&
    (isBundleAlwaysBlocked || (isPriceTest && !bundleProductsConfig));

  const selectedProductCount = useSelector(getProductPickerSelectedCount);
  const isAtProductLimit =
    isCheckoutBlock &&
    selectedProductCount >= CHECKOUT_BLOCK_UPSELL_MAX_PRODUCTS;
  const isProductAlreadySelected =
    checkboxState.checked || checkboxState.indeterminate;
  const shouldDisableDueToLimit = isAtProductLimit && !isProductAlreadySelected;

  const handleToggle = () => {
    if (isBundleDisabled && !checked) {
      return;
    }

    const baseProduct = {
      id: product.id,
      title: product.title,
      image: product.image,
      handle: product.handle,
      requiresSellingPlan: product.requiresSellingPlan,
      isBundle: product.isBundle,
    };

    if (isVariantRow && variant) {
      dispatch(
        toggleProductVariant({
          product: baseProduct,
          variantId: variant.id,
          allVariantIds,
        })
      );
    } else if (
      (isCheckoutBlock || isCartTargeting) &&
      product.variants?.length === 1
    ) {
      dispatch(
        toggleProductVariant({
          product: baseProduct,
          variantId: product.variants[0].id,
          allVariantIds,
        })
      );
    } else {
      dispatch(toggleProduct(baseProduct));
    }
  };

  return (
    <GenericFlexStartContainer>
      <IgTooltip
        title={
          isBundleDisabled ? (
            <IgTypography
              variant="body2"
              color={theme.palette.text.secondary}
              onClick={
                isBundleAlwaysBlocked
                  ? undefined
                  : () =>
                      openNewTab(
                        bundlePriceTesting,
                        "Price Testing with Bundle Products"
                      )
              }
              sx={{
                ...(!isBundleAlwaysBlocked && {
                  "&:hover": {
                    cursor: "pointer",
                    textDecoration: "underline",
                  },
                }),
                marginTop: "0px",
                display: "block",
              }}
            >
              {isCheckoutBlock
                ? "Bundle products are not supported in upsell tests."
                : isCartTargeting
                  ? "Bundle products are not supported in cart targeting."
                  : "Bundle products are not supported in price tests."}
            </IgTypography>
          ) : shouldDisableDueToLimit ? (
            <IgTypography variant="body2" color={theme.palette.text.secondary}>
              Maximum of {CHECKOUT_BLOCK_UPSELL_MAX_PRODUCTS} products allowed
            </IgTypography>
          ) : (
            ""
          )
        }
      >
        <IgCheckbox
          disabled={shouldDisableDueToLimit || isBundleDisabled}
          checked={checked}
          indeterminate={indeterminate}
          onChange={handleToggle}
        />
      </IgTooltip>
    </GenericFlexStartContainer>
  );
};
